import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import styled from "styled-components";

const REQUEST_MAGIC_LINK = gql`
  mutation RequestMagicLink($email: String!) {
    requestMagicLink(email: $email) {
      ok
      message
    }
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const Card = styled.div`
  background: white;
  padding: 36px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 420px;
  width: 100%;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 12px;
  font-size: 22px;
`;

const Message = styled.p`
  color: #666;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const Button = styled.button`
  background-color: #1976d2;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1565c0;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Small = styled.p`
  color: #888;
  font-size: 12px;
  margin-top: 8px;
`;

const ErrorText = styled.p`
  color: #d32f2f;
  margin-top: 4px;
`;

const SuccessText = styled.p`
  color: #2e7d32;
  margin-top: 4px;
`;

const RequestMagicLinkPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [requestMagicLink, { loading, error, data }] =
    useMutation(REQUEST_MAGIC_LINK);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setValidationError("Please enter a valid email address.");
      return;
    }

    try {
      await requestMagicLink({ variables: { email: trimmed } });
      setSubmitted(true);
    } catch (err) {
      // Note: Backend may silently reject ADMIN users (they use Okta/CloudFlare auth)
      // We still show success message to prevent user enumeration
      setSubmitted(true);
    }
  };

  const successMessage =
    data?.requestMagicLink?.message ||
    "If an account exists with that email, a magic link has been sent.";

  return (
    <Container>
      <Card>
        <Title>Request a Sign-in Link</Title>
        <Message>
          Enter your email. If an account exists, we will send you a one-time
          sign-in link valid for 10 minutes.
        </Message>
        <Small style={{ marginBottom: "12px", color: "#666" }}>
          <strong>Note:</strong> Admin users should authenticate via
          Okta/CloudFlare SSO instead.
        </Small>
        {submitted ? (
          <>
            <SuccessText>{successMessage}</SuccessText>
            <Small>Check your email inbox (and spam folder).</Small>
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {validationError && <ErrorText>{validationError}</ErrorText>}
            {error && (
              <Small>
                {" "}
                Note: We always respond the same way for your security.
              </Small>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Magic Link"}
            </Button>
            <Small>
              This does not reveal whether an account exists for that email.
            </Small>
          </Form>
        )}
      </Card>
    </Container>
  );
};

export default RequestMagicLinkPage;
